import { Link } from 'react-router-dom';
import SEOHelmet from '../SEOHelmet';

export default function ServicePageTemplate({ service }) {
  return (
    <>
      <SEOHelmet
        title={`${service.eyebrow} | AxentraLab`}
        description={service.summary}
        keywords={`${service.eyebrow}, software development, AxentraLab, web development, cloud, SaaS`}
      />
      <div style={{ background: 'var(--bg)', minHeight: '100vh', overflowX: 'hidden' }}>
        <style>{`
          @keyframes fadeUp { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: none; } }
          .service-shell { max-width: 1180px; margin: 0 auto; padding: 0 5%; }
          .service-label {
            display: inline-flex; align-items: center; gap: 10px; color: ${service.accent};
            font-family: var(--font-m); font-size: 12px; font-weight: 800; letter-spacing: 1.5px;
            text-transform: uppercase; margin-bottom: 18px;
          }
          .service-label::before { content: ''; width: 28px; height: 2px; border-radius: 99px; background: ${service.accent}; }
          .service-grid, .process-grid, .stack-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; }
          .capability-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px; }
          .service-card {
            border: 1px solid var(--border); border-radius: 8px; background: #fff; padding: 24px;
            transition: transform .2s ease, border-color .2s ease, box-shadow .2s ease;
          }
          .service-card:hover { transform: translateY(-4px); border-color: ${service.accent}55; box-shadow: 0 20px 44px -30px rgba(15,23,42,.45); }
          .stack-pill {
            min-height: 42px; display: grid; place-items: center; border: 1px solid var(--border);
            border-radius: 8px; background: #fff; color: var(--muted); font-family: var(--font-m);
            font-size: 12px; font-weight: 800; text-align: center; padding: 8px;
          }
          @media (max-width: 980px) {
            .service-grid, .process-grid, .stack-grid, .capability-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
            .hero-layout, .cta-layout { grid-template-columns: 1fr !important; }
          }
          @media (max-width: 680px) {
            .service-grid, .process-grid, .stack-grid, .capability-grid { grid-template-columns: 1fr; }
            .hero-title { font-size: 42px !important; }
          }
          @media (prefers-reduced-motion: reduce) { *, *::before, *::after { animation: none !important; transition: none !important; } }
        `}</style>

        <section style={{ padding: '150px 0 76px', position: 'relative', overflow: 'hidden', background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)' }}>
          <div style={{
            position: 'absolute', inset: 0,
            backgroundImage: 'linear-gradient(var(--border) 1px,transparent 1px),linear-gradient(90deg,var(--border) 1px,transparent 1px)',
            backgroundSize: '64px 64px', opacity: 0.44,
          }} />
          <div className="service-shell hero-layout" style={{ position: 'relative', zIndex: 1, display: 'grid', gridTemplateColumns: '1.05fr .95fr', gap: 42, alignItems: 'center' }}>
            <div style={{ animation: 'fadeUp .7s ease both' }}>
              <div className="service-label">{service.eyebrow}</div>
              <h1 className="hero-title" style={{ fontFamily: 'var(--font-h)', fontSize: 'clamp(48px, 7vw, 82px)', lineHeight: 0.98, color: 'var(--text)', margin: '0 0 24px', fontWeight: 900 }}>
                {service.title}
              </h1>
              <p style={{ color: 'var(--muted)', fontSize: 18, lineHeight: 1.75, maxWidth: 680, marginBottom: 34 }}>
                {service.summary}
              </p>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <Link to="/contact" className="btn-primary" style={{ padding: '15px 30px', background: service.accent }}>
                  Start This Service -&gt;
                </Link>
                <Link to="/portfolio" className="btn-outline" style={{ padding: '15px 28px', color: service.accent, borderColor: service.accent }}>
                  View Work
                </Link>
              </div>
            </div>
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, background: '#fff', overflow: 'hidden', boxShadow: '0 24px 60px -36px rgba(15,23,42,.45)' }}>
              {[
                ['Timeline', service.timeline],
                ['Best for', service.bestFor],
                ['Engagement', 'Fixed scope or retainer'],
                ['Handover', 'Docs, source, and support'],
              ].map(([label, value]) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: 20, padding: '22px 24px', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ color: 'var(--muted)', fontFamily: 'var(--font-m)', fontSize: 12, fontWeight: 800 }}>{label}</span>
                  <span style={{ color: 'var(--text)', fontWeight: 800, textAlign: 'right' }}>{value}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="service-shell" style={{ padding: '76px 5% 92px' }}>
          <div style={{ marginBottom: 34 }}>
            <div className="service-label">Outcomes</div>
            <h2 style={{ fontFamily: 'var(--font-h)', fontSize: 'clamp(30px,4.5vw,52px)', lineHeight: 1.05, color: 'var(--text)', margin: 0, fontWeight: 900 }}>
              What you get from this service.
            </h2>
          </div>
          <div className="service-grid">
            {service.outcomes.map((outcome, index) => (
              <div key={outcome} className="service-card">
                <div style={{ color: service.accent, fontFamily: 'var(--font-h)', fontSize: 28, fontWeight: 900, marginBottom: 16 }}>{String(index + 1).padStart(2, '0')}</div>
                <div style={{ color: 'var(--text)', fontSize: 18, fontWeight: 850, lineHeight: 1.25 }}>{outcome}</div>
              </div>
            ))}
          </div>
        </section>

        <section style={{ padding: '82px 0', background: '#fff', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
          <div className="service-shell">
            <div style={{ textAlign: 'center', marginBottom: 42 }}>
              <div className="service-label" style={{ justifyContent: 'center' }}>Capabilities</div>
              <h2 style={{ fontFamily: 'var(--font-h)', fontSize: 'clamp(30px,4.5vw,48px)', color: 'var(--text)', margin: 0, fontWeight: 900 }}>
                Scope built around business workflow.
              </h2>
            </div>
            <div className="capability-grid">
              {service.capabilities.map((item) => (
                <div key={item} className="service-card" style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <span style={{ color: service.accent, fontWeight: 900 }}>-</span>
                  <span style={{ color: 'var(--text)', fontSize: 15, lineHeight: 1.55, fontWeight: 700 }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="service-shell" style={{ padding: '82px 5% 94px' }}>
          <div style={{ textAlign: 'center', marginBottom: 42 }}>
            <div className="service-label" style={{ justifyContent: 'center' }}>Process</div>
            <h2 style={{ fontFamily: 'var(--font-h)', fontSize: 'clamp(30px,4.5vw,48px)', color: 'var(--text)', margin: 0, fontWeight: 900 }}>
              A practical path from request to launch.
            </h2>
          </div>
          <div className="process-grid">
            {service.process.map(([step, title, desc]) => (
              <div key={step} className="service-card">
                <div style={{ color: service.accent, fontFamily: 'var(--font-h)', fontSize: 30, fontWeight: 900, marginBottom: 18 }}>{step}</div>
                <h3 style={{ color: 'var(--text)', fontFamily: 'var(--font-h)', fontSize: 20, fontWeight: 850, margin: '0 0 10px' }}>{title}</h3>
                <p style={{ color: 'var(--muted)', fontSize: 14, lineHeight: 1.7 }}>{desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section style={{ padding: '76px 0', background: '#fff', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
          <div className="service-shell">
            <div className="service-label">Technology Stack</div>
            <div className="stack-grid">
              {service.stack.map((tech) => <div key={tech} className="stack-pill">{tech}</div>)}
            </div>
          </div>
        </section>

        <section className="service-shell" style={{ padding: '94px 5% 110px' }}>
          <div className="cta-layout" style={{ border: '1px solid var(--border)', borderRadius: 8, background: '#0f172a', color: '#fff', padding: 'clamp(42px,7vw,76px)', display: 'grid', gridTemplateColumns: '1fr auto', gap: 28, alignItems: 'center' }}>
            <div>
              <div style={{ color: '#93c5fd', fontFamily: 'var(--font-m)', fontSize: 12, fontWeight: 800, textTransform: 'uppercase', marginBottom: 16 }}>Build with AxentraLab</div>
              <h2 style={{ fontFamily: 'var(--font-h)', fontSize: 'clamp(32px,5vw,58px)', lineHeight: 1.02, margin: '0 0 18px', fontWeight: 900 }}>
                Ready to scope {service.eyebrow.toLowerCase()}?
              </h2>
              <p style={{ color: '#cbd5e1', fontSize: 16, lineHeight: 1.7, maxWidth: 620 }}>
                Share your current goal, constraints, and timeline. We will recommend a practical next step and realistic delivery plan.
              </p>
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <Link to="/contact" className="btn-primary" style={{ padding: '15px 28px', background: '#fff', color: '#0f172a' }}>Get Proposal</Link>
              <Link to="/services" className="btn-outline" style={{ padding: '15px 26px', color: '#fff', borderColor: 'rgba(255,255,255,.36)' }}>All Services</Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
